/* eslint-disable jira/naming/disallow-filenames */
/** @jsx jsx */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import { cssMap, jsx } from '@compiled/react';
import { getErrorHash } from '@jira/platform__error-reporting/src/getErrorHash.tsx';
import Heading from '@atlaskit/heading/heading';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import errorImg from './assets/generic-error.svg';

const errorHeading = defineMessage({
	id: 'spa-apps-sidebar-common.common.error-boundary.fallback.error-heading',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Something went wrong',
	description:
		'Heading text shown in the sidebar error boundary fallback when a component fails to load due to an unexpected error',
});

const errorBody = defineMessage({
	id: 'spa-apps-sidebar-common.common.error-boundary.fallback.error-body',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Try refreshing or navigating to another page.',
	description:
		'Body text shown below the error heading in the sidebar error boundary fallback, suggesting recovery actions to the user',
});

const styles = cssMap({
	wrapper: {
		backgroundColor: 'color.background.neutral',
		color: 'color.text.subtle',
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		textAlign: 'center',
		alignItems: 'center',
		gap: token('space.100'),
		display: 'flex',
		flexDirection: 'column',
	},
	error: {
		borderRadius: 'radius.xsmall',
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
		backgroundColor: token('color.background.accent.gray.subtler'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},
	image: {
		height: '50px',
	},
});

type FallbackProps = {
	error: Error;
};

export const Fallback = (props: FallbackProps): JSX.Element => {
	const { error } = props;
	const { formatMessage } = useIntl();

	return (
		<div css={styles.wrapper}>
			<img css={styles.image} alt="" src={errorImg} />
			<Heading size="xsmall">{formatMessage(errorHeading)}</Heading>
			<>{formatMessage(errorBody)}</>
			{/* eslint-disable-next-line @atlaskit/design-system/no-html-code */}
			<code css={styles.error}>ERROR: {getErrorHash(error)}</code>
		</div>
	);
};
